import { Divider } from '@mui/material'
import {
  TABLE_WIDTH_BRAND,
  TABLE_WIDTH_FOUND,
  TABLE_WIDTH_MODEL,
} from 'lib/tableLib'
import T from 'prop-types'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { isRepairSession } from 'lib/sessionLifecycle'

import ConfirmBar from 'components/atoms/ConfirmBar'
import DotCell from 'components/atoms/DotCell'
import DownloadBar from 'components/atoms/DownloadBar'
import DropArea from 'components/atoms/DropArea'
import FilterSelect from 'components/atoms/FilterSelect'
import SearchField from 'components/atoms/SearchField'
import Table from 'components/atoms/Table'
import TableHeaderCell from 'components/atoms/TableHeaderCell'
import TitleBar from 'components/atoms/TitleBar'
import UploadBar from 'components/atoms/UploadBar'

import useCarsFilters from 'hooks/useCarsFilters'
import useInputOutput, {
  BASE_FILE_UPLOAD_EXTENSIONS,
} from 'hooks/useInputOutput'

import SessionsDownloadTemplate from '../Sessions/SessionsDownloadTemplate'
import SessionsSearchBar from '../Sessions/SessionsSearchBar'

const createColumns = (t, cars, carsFilters) => {
  const {
    filters,
    filtersData,
    setBrands,
    setModels,
    setExists,
    setPlateQuery,
  } = carsFilters

  return [
    {
      accessor: 'brand',
      Header: (
        <TableHeaderCell
          label={t('sessions.cars.table.brand')}
          filter={
            <FilterSelect
              data-testid="search-brand"
              options={filtersData.brands}
              value={filters.brands}
              handleChange={setBrands}
              multiple
              hasSearchField
              searchLabel={t('common.form.searchCarBrand')}
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_BRAND,
        },
      },
    },
    {
      accessor: 'model',
      Header: (
        <TableHeaderCell
          label={t('sessions.cars.table.model')}
          filter={
            <FilterSelect
              data-testid="search-model"
              options={filtersData.models}
              value={filters.models}
              handleChange={setModels}
              multiple
              hasSearchField
              searchLabel={t('common.form.searchCarModel')}
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_MODEL,
        },
      },
    },
    {
      accessor: 'plate',
      Header: (
        <TableHeaderCell
          label={t('sessions.cars.table.plate')}
          filter={
            <SearchField
              data-testid="search-plate"
              placeholder={t('common.form.searchCarPlate')}
              value={filters.plateQuery}
              onChange={(event) => setPlateQuery(event.target.value)}
              fullWidth={false}
            />
          }
        />
      ),
    },
    {
      accessor: 'exists',
      Header: (
        <TableHeaderCell
          label={t('sessions.cars.table.exists')}
          errorsNumber={cars.filter((car) => !car.exists).length}
          filter={
            <FilterSelect
              options={filtersData.exists}
              value={filters.exists}
              handleChange={setExists}
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_FOUND,
        },
      },
    },
  ]
}

export default function SessionsCars({
  session,
  stepCars,
  updateCars,
  handleIngestCars,
  handleValidation,
  handleConfirm,
  confirmed,
  readOnly,
  loading = false,
  contentIsReadOnly = false,
  uploadIsReadOnly = false,
}) {
  const { t } = useTranslation()

  const { step } = useParams()
  const repairSession = isRepairSession(session)
  const carsFilters = useCarsFilters()
  const { filters, initFilters, setQuery, filterCars, resetFilters } =
    carsFilters

  useEffect(() => {
    if (!stepCars.length) {
      return
    }

    initFilters(stepCars)
  }, [stepCars, initFilters])

  const filteredCars = useMemo(
    () => filterCars(stepCars, filters),
    [filterCars, stepCars, filters]
  )

  const columns = useMemo(
    () => createColumns(t, stepCars, carsFilters),
    [t, stepCars, carsFilters]
  )

  const handleIngest = useCallback(() => {
    if (uploadIsReadOnly) {
      return
    }

    handleIngestCars(session?.sessionId)
    resetFilters()
  }, [session, handleIngestCars, resetFilters, uploadIsReadOnly])

  const handleUpdate = useCallback(
    (cars, fileName) => {
      if (uploadIsReadOnly) {
        return
      }

      updateCars(session?.sessionId, cars, fileName, true)
      resetFilters()
    },
    [resetFilters, session?.sessionId, updateCars, uploadIsReadOnly]
  )

  const { handleDrop, handleSelectFile, saveXls } = useInputOutput(handleUpdate)

  const handleDownload = async () => {
    const rows = [
      [
        t('common.excel.plate'),
        t('common.excel.plateCheck'),
        t('common.excel.name'),
      ],
      ...stepCars.map((car) => [
        car.plate,
        car.exists
          ? t('sessions.pricing.carFoundOk')
          : t('sessions.error.plateMissing'),
        `${car.title}`,
      ]),
    ]
    saveXls('cars', rows)
  }

  const preparedItems = filteredCars.map((car) => {
    return {
      ...car,
      exists: (
        <DotCell
          color={car.exists ? 'primary' : 'error'}
          label={
            car.exists
              ? t('sessions.cars.carFoundOk')
              : t('sessions.cars.carFoundMissing')
          }
        />
      ),
    }
  })

  return (
    <div data-testid="session-cars">
      <TitleBar
        title={t(
          repairSession
            ? 'sessions.cars.repair.contentTitle'
            : 'sessions.cars.contentTitle'
        )}
        description={t(
          repairSession
            ? 'sessions.cars.repair.contentDescription'
            : 'sessions.cars.contentDescription'
        )}
        rightColumn={
          <UploadBar
            id={step}
            fileName={session?.[`${step}FileName`]}
            readOnly={readOnly || contentIsReadOnly || uploadIsReadOnly}
            loading={loading}
            uploaded={!!stepCars.length}
            handleSelectFile={handleSelectFile}
            ingestLabel={t(`common.form.ingestButtonCars`)}
            onIngest={handleIngest}
          />
        }
      />
      {stepCars.length ? (
        <>
          <Table
            id="cars-list"
            items={preparedItems}
            columns={columns}
            top={
              <>
                <ConfirmBar
                  component="div"
                  label={t(`sessions.cars.confirmButton`)}
                  disabled={
                    !handleValidation() ||
                    confirmed ||
                    readOnly ||
                    contentIsReadOnly
                  }
                  left={
                    <SessionsSearchBar
                      id="text"
                      label={t('sessions.cars.platesUploaded')}
                      filteredLabel={t('common.filters.filteredPlates')}
                      cars={stepCars}
                      filteredCars={filteredCars}
                      query={filters.query}
                      handleChangeSearch={(event) =>
                        setQuery(event.target.value)
                      }
                    />
                  }
                  leftProps={{ sx: { width: 0.5 } }}
                  right={
                    <DownloadBar
                      readOnly={readOnly || contentIsReadOnly}
                      handleDownload={handleDownload}
                    />
                  }
                  handleConfirm={handleConfirm}
                />
                <Divider />
              </>
            }
          />
        </>
      ) : (
        <>
          <DropArea
            onDrop={handleDrop}
            disabled={readOnly || contentIsReadOnly || uploadIsReadOnly}
            accept={BASE_FILE_UPLOAD_EXTENSIONS.toString()}
          />
          <SessionsDownloadTemplate
            label={t('sessions.cars.downloadTemplateFile')}
            handleDownload={handleDownload}
          />
        </>
      )}
    </div>
  )
}

SessionsCars.propTypes = {
  session: T.object.isRequired,
  stepCars: T.array.isRequired,
  updateCars: T.func.isRequired,
  handleValidation: T.func.isRequired,
  handleConfirm: T.func.isRequired,
  confirmed: T.bool,
  readOnly: T.bool,
  loading: T.bool,
  contentIsReadOnly: T.bool,
  uploadIsReadOnly: T.bool,
}
